﻿import {
  useGetV4ManufacturerByRouteManufacturerIdDocumentPurchaseOrdersQuery,
  useGetV4ManufacturerByRouteManufacturerIdDocumentsInvoicesQuery,
  useGetV4ManufacturerByRouteManufacturerIdDocumentsReceiptsQuery,
  useGetV4ManufacturerByRouteManufacturerIdDocumentsRequisitionsQuery,
  useGetV4ManufacturerByRouteManufacturerIdInventoryValueQuery,
} from "randmar-api-client";
import {
  Alert,
  AlertDescription,
  AlertTitle,
  Card,
  CardContent,
  RmBodyZone,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "randmarcomps";
import * as React from "react";
import { Link, useParams } from "react-router-dom";
import VendorValues from "../components/categories/VendorValues";
import InvoicesGrid from "../components/grids/InvoicesGrid";
import PurchaseOrdersGrid from "../components/grids/PurchaseOrdersGrid";
import ReceiptsGrid from "../components/grids/ReceiptsGrid";
import DataRefreshAssistantCluster from "../components/layout/DataRefreshAssistantCluster";
import RmPageHeader from "../components/layout/RmPageHeader";
import { useManufacturer } from "../contexts/WithManufacturer";
import Path from "../routes/Path";
import withRoot from "../withRoot";

type OperationsTab = "Overview" | "PurchaseOrders" | "Receipts" | "Invoices";

function buildOperationsAssistantPrompt(options: {
  applicationId: string;
  activeTab: OperationsTab;
  warehousesLoaded: number;
  purchaseOrderLinesLoaded: number;
  receiptLinesLoaded: number;
  invoiceLinesLoaded: number;
  requisitionsLoaded: number;
}) {
  return [
    "You are assisting with the Randmar Manufacturer Portal Operations page.",
    `Manufacturer applicationId: ${options.applicationId}`,
    `Active tab: ${options.activeTab}`,
    "",
    `Inventory warehouses loaded: ${options.warehousesLoaded}`,
    `Purchase order lines loaded: ${options.purchaseOrderLinesLoaded}`,
    `Receipt lines loaded: ${options.receiptLinesLoaded}`,
    `Invoice lines loaded: ${options.invoiceLinesLoaded}`,
    `Draft requisitions loaded: ${options.requisitionsLoaded}`,
    "",
    "Help me interpret what I'm seeing and recommend next actions.",
  ].join("\n");
}

const Operations: React.FC = () => {
  const { applicationId } = useManufacturer();
  const { subSection } = useParams();

  const [lastRefreshedAt, setLastRefreshedAt] = React.useState<Date | null>(null);

  const inventoryQuery = useGetV4ManufacturerByRouteManufacturerIdInventoryValueQuery(
    { routeManufacturerId: applicationId },
    { skip: !applicationId }
  );
  const requisitionsQuery = useGetV4ManufacturerByRouteManufacturerIdDocumentsRequisitionsQuery(
    { routeManufacturerId: applicationId },
    { skip: !applicationId }
  );
  const purchaseOrdersQuery = useGetV4ManufacturerByRouteManufacturerIdDocumentPurchaseOrdersQuery(
    { routeManufacturerId: applicationId },
    { skip: !applicationId }
  );
  const receiptsQuery = useGetV4ManufacturerByRouteManufacturerIdDocumentsReceiptsQuery(
    { routeManufacturerId: applicationId },
    { skip: !applicationId }
  );
  const invoicesQuery = useGetV4ManufacturerByRouteManufacturerIdDocumentsInvoicesQuery(
    { routeManufacturerId: applicationId },
    { skip: !applicationId }
  );

  const inventoryValues = inventoryQuery.data ?? [];
  const requisitions = requisitionsQuery.data ?? [];
  const purchaseOrders = purchaseOrdersQuery.data ?? [];
  const receipts = receiptsQuery.data ?? [];
  const invoices = invoicesQuery.data ?? [];

  const isRefreshing =
    inventoryQuery.isFetching ||
    requisitionsQuery.isFetching ||
    purchaseOrdersQuery.isFetching ||
    receiptsQuery.isFetching ||
    invoicesQuery.isFetching;

  const hasLoadedSomeData =
    inventoryQuery.data !== undefined ||
    requisitionsQuery.data !== undefined ||
    purchaseOrdersQuery.data !== undefined ||
    receiptsQuery.data !== undefined ||
    invoicesQuery.data !== undefined;

  const hasErrors =
    inventoryQuery.isError ||
    requisitionsQuery.isError ||
    purchaseOrdersQuery.isError ||
    receiptsQuery.isError ||
    invoicesQuery.isError;

  const hasSettledInitialLoad =
    !inventoryQuery.isLoading &&
    !requisitionsQuery.isLoading &&
    !purchaseOrdersQuery.isLoading &&
    !receiptsQuery.isLoading &&
    !invoicesQuery.isLoading;

  React.useEffect(() => {
    const previousTitle = document.title;
    document.title = "Operations - Manufacturer";

    return () => {
      document.title = previousTitle;
    };
  }, []);

  React.useEffect(() => {
    if (lastRefreshedAt) return;
    if (!hasLoadedSomeData) return;
    if (!hasSettledInitialLoad) return;
    if (hasErrors) return;

    setLastRefreshedAt(new Date());
  }, [hasErrors, hasLoadedSomeData, hasSettledInitialLoad, lastRefreshedAt]);

  const handleRefresh = React.useCallback(async () => {
    if (!applicationId) return;

    const results = await Promise.all([
      inventoryQuery.refetch(),
      requisitionsQuery.refetch(),
      purchaseOrdersQuery.refetch(),
      receiptsQuery.refetch(),
      invoicesQuery.refetch(),
    ]);

    const refreshedSuccessfully = results.every((result) => !("error" in result && Boolean(result.error)));
    if (refreshedSuccessfully) {
      setLastRefreshedAt(new Date());
    }
  }, [applicationId, inventoryQuery, invoicesQuery, purchaseOrdersQuery, receiptsQuery, requisitionsQuery]);

  const currentTab = React.useMemo<OperationsTab>(() => {
    if (subSection === "PurchaseOrders" || subSection === "Receipts" || subSection === "Invoices" || subSection === "Overview") {
      return subSection;
    }
    return "Overview";
  }, [subSection]);

  const warehouseCount = React.useMemo(() => {
    const entries = inventoryValues
      .map((entry) => entry.Location)
      .filter((location): location is string => Boolean(location));
    return new Set(entries).size;
  }, [inventoryValues]);

  const assistantPrompt = React.useMemo(
    () =>
      buildOperationsAssistantPrompt({
        applicationId,
        activeTab: currentTab,
        warehousesLoaded: warehouseCount,
        purchaseOrderLinesLoaded: purchaseOrders.length,
        receiptLinesLoaded: receipts.length,
        invoiceLinesLoaded: invoices.length,
        requisitionsLoaded: requisitions.length,
      }),
    [applicationId, currentTab, invoices.length, purchaseOrders.length, receipts.length, requisitions.length, warehouseCount]
  );

  return (
    <Tabs value={currentTab} className="w-full">
      <RmPageHeader
        title="Operations"
        subtitle="Monitor inventory and operational documents across Randmar warehouses."
        rightTopActions={(
          <DataRefreshAssistantCluster
            lastRefreshAt={lastRefreshedAt}
            isRefreshing={isRefreshing}
            onRefresh={handleRefresh}
            assistantPromptOptions={{ prompt: assistantPrompt }}
          />
        )}
        secondaryRow={(
          <TabsList className="flex h-auto w-full flex-wrap items-center justify-start gap-2 rounded-none border-b border-border bg-transparent p-0">
            <TabsTrigger
              value="Overview"
              className="relative h-12 rounded-none border-b-2 border-b-transparent bg-transparent px-4 pb-3 pt-2 text-sm font-medium text-muted-foreground shadow-none transition-none hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background data-[state=active]:border-b-primary data-[state=active]:text-foreground"
              asChild
            >
              <Link to={Path.operationsSubSection(applicationId, "Overview")}>Inventory</Link>
            </TabsTrigger>
            <TabsTrigger
              value="PurchaseOrders"
              className="relative h-12 rounded-none border-b-2 border-b-transparent bg-transparent px-4 pb-3 pt-2 text-sm font-medium text-muted-foreground shadow-none transition-none hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background data-[state=active]:border-b-primary data-[state=active]:text-foreground"
              asChild
            >
              <Link to={Path.operationsSubSection(applicationId, "PurchaseOrders")}>Purchase Orders</Link>
            </TabsTrigger>
            <TabsTrigger
              value="Receipts"
              className="relative h-12 rounded-none border-b-2 border-b-transparent bg-transparent px-4 pb-3 pt-2 text-sm font-medium text-muted-foreground shadow-none transition-none hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background data-[state=active]:border-b-primary data-[state=active]:text-foreground"
              asChild
            >
              <Link to={Path.operationsSubSection(applicationId, "Receipts")}>Receipts</Link>
            </TabsTrigger>
            <TabsTrigger
              value="Invoices"
              className="relative h-12 rounded-none border-b-2 border-b-transparent bg-transparent px-4 pb-3 pt-2 text-sm font-medium text-muted-foreground shadow-none transition-none hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background data-[state=active]:border-b-primary data-[state=active]:text-foreground"
              asChild
            >
              <Link to={Path.operationsSubSection(applicationId, "Invoices")}>Invoices</Link>
            </TabsTrigger>
          </TabsList>
        )}
      />

      <RmBodyZone className="space-y-8 pb-10" aria-busy={isRefreshing}>
        {hasErrors && (
          <Alert variant="destructive" className="rounded-2xl border border-destructive/30 bg-card">
            <AlertTitle>Couldn't load operations data</AlertTitle>
            <AlertDescription>Try refreshing. If the issue persists, contact support.</AlertDescription>
          </Alert>
        )}

        <section className="w-full" aria-label="Operations details">
          <TabsContent value="Overview" className="space-y-6">
            <Card className="w-full rounded-2xl border border-gray-100 bg-white shadow-sm">
              <CardContent className="p-6">
                <VendorValues inventoryValues={inventoryValues} requisitions={requisitions} />
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="PurchaseOrders" className="space-y-6">
            <Card className="w-full rounded-2xl border border-gray-100 bg-white shadow-sm">
              <CardContent className="p-6">
                <PurchaseOrdersGrid purchaseOrders={purchaseOrders} />
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="Receipts" className="space-y-6">
            <Card className="w-full rounded-2xl border border-gray-100 bg-white shadow-sm">
              <CardContent className="p-6">
                <ReceiptsGrid receipts={receipts} />
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="Invoices" className="space-y-6">
            <Card className="w-full rounded-2xl border border-gray-100 bg-white shadow-sm">
              <CardContent className="p-6">
                <InvoicesGrid invoices={invoices} />
              </CardContent>
            </Card>
          </TabsContent>
        </section>
      </RmBodyZone>
    </Tabs>
  );
};

export default withRoot(Operations);
